import React from 'react';
import { Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLang } from '../i18n/LanguageContext';
import { directors } from '../mock';
import Tilt3D from './Tilt3D';

export default function Team() {
  const { t } = useLang();

  return (
    <section className="py-24 bg-white overflow-hidden" data-testid="team-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
            className="text-[color:var(--brand)] uppercase tracking-[0.4em] text-xs mb-4"
          >
            {t('team.eyebrow')}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl md:text-5xl font-light text-[color:var(--ink)] mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t('team.title')}
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mx-auto w-16 h-[2px] bg-[color:var(--brand)]"
          />
        </div>

        <div className="flex flex-wrap justify-center gap-8">
          {directors.map((d, i) => (
            <motion.div
              key={d.name}
              data-testid={`director-card-${i}`}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.75, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-[340px]"
            >
              <Tilt3D className="director-card group bg-white rounded-lg overflow-hidden border border-[color:var(--line)] shadow-sm hover:shadow-2xl transition-shadow duration-500">
              <div className="relative overflow-hidden bg-[color:var(--brand-soft)]/40">
                <img
                  src={d.photo}
                  alt={`${d.name} — ${d.designation}, Vedyah Exim`}
                  data-testid={`director-photo-${i}`}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  className="w-full aspect-[5/4] object-cover object-top block select-none transition-transform duration-700 group-hover:scale-[1.02]"
                />
                {/* Transparent overlay: intercepts right-click/drag so "Save Image As" never targets the real <img>. */}
                <div
                  className="absolute inset-0 z-10"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />
              </div>
              <div className="p-6 bg-[color:var(--brand-soft)]/30">
                <Quote className="text-[color:var(--brand)] mb-3" size={24} />
                <p className="text-[color:var(--ink)] leading-relaxed mb-5 text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>
                  "{d.quote}"
                </p>
                <div className="h-[1px] w-14 bg-[color:var(--brand)] mb-3" />
                <div className="font-semibold text-base text-[color:var(--ink)]" data-testid={`director-name-${i}`}>{d.name}</div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-[color:var(--brand)] mt-1" data-testid={`director-role-${i}`}>{d.designation} — Vedyah Exim</div>
              </div>
              </Tilt3D>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
